import { version } from '../../../package.json';

export type BaseErrorParameters = {
  /** The underlying error that caused this error, if any */
  cause: Error | null;
  /** The error unique code */
  code: string;
  /** Additional detailed information about the error */
  details?: string;
  /** URL to relevant documentation for this error */
  docsUrl: string | null;
  /** Array of additional contextual messages to be displayed */
  metaMessages?: string[];
  /** Custom name for the error. Defaults to 'BaseError' if not provided */
  name: string;
  /** A brief, human-readable description of the error */
  shortMessage: string;
};

const getDetails = ({ details, cause }: BaseErrorParameters) => {
  if (cause instanceof BaseError) {
    return cause.details;
  }

  if (cause?.message) {
    return cause.message;
  }

  return details;
};

/**
 * Formats the error message with all available information
 */
const formatMessage = ({
  shortMessage,
  details,
  docsUrl,
  metaMessages,
}: BaseErrorParameters) => {
  return [
    '[Dynamic JS SDK]',
    shortMessage,
    '',
    ...(metaMessages ? [...metaMessages, ''] : []),
    ...(docsUrl ? [`Docs: ${docsUrl}`] : []),
    ...(details ? [`Details: ${details}`] : []),
    `Version: ${version}`,
    `Timestamp: ${new Date().toISOString()}`,
  ].join('\n');
};

/**
 * Base error class that provides structured error handling with detailed information
 */
export abstract class BaseError extends Error {
  /** The error unique code */
  code: string;
  details: string | undefined;
  private readonly formattedMessage: string | undefined;
  override name = 'BaseError';
  override cause: BaseError | Error | undefined;

  constructor(args: BaseErrorParameters) {
    const details = getDetails(args);
    const formattedMessage = formatMessage({ ...args, details });
    super(
      args.shortMessage ?? formattedMessage,
      args.cause ? { cause: args.cause } : undefined
    );

    this.formattedMessage = formattedMessage;
    this.details = details;
    this.name = args.name ?? this.name;
    this.cause = args.cause ?? this.cause;
    this.code = args.code;
  }

  /**
   * Walks the cause chain of the error and returns the root error
   */
  walk(): Error | undefined {
    const cause = this.cause;

    if (cause instanceof BaseError) {
      return cause.walk();
    }

    return cause;
  }

  override toString() {
    return this.formattedMessage;
  }
}
